<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div
            class="tw-mx-auto tw-w-[95%] md:tw-max-w-[700px] tw-overflow-hidden tw-h-fit tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-shadow-md tw-my-5 tw-rounded">
            <div class="tw-p-4 tw-text-lg tw-border-b tw-border-solid tw-font-medium tw-text-gray-600 tw-bg-gray-50">
                {{ isEdit ? $t('portal.customers.edit') : $t('portal.customers.create') }}
            </div>

            <div class="tw-p-5 tw-bg-white">
                <div class="tw-grid tw-grid-cols-12 tw-gap-x-4">
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormField left-icon="solar:user-circle-bold-duotone" v-model="form.name" :label="$t('fields.name')" type="text" :error="err('name')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormField left-icon="solar:letter-bold-duotone" v-model="form.email" :label="$t('fields.email')" type="text" :error="err('email')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormField v-model="form.phone" :label="$t('fields.customer_phone')" type="text" :error="err('phone')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormField v-model="form.country" :label="$t('portal.fields.country')" type="text" :error="err('country')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-6">
                        <FormField v-model="form.payment_status" :label="$t('portal.fields.payment-status')" type="select" :options="paymentOptions" :error="err('payment_status')" />
                    </div>
                    <div class="tw-col-span-12">
                        <FormField v-model="form.note" :label="$t('fields.customer_notes')" type="textarea" :error="err('note')" />
                    </div>

                    <!-- Every customer gets a portal login; the password can be set now or edited later. -->
                    <div v-if="!isEdit" class="tw-col-span-12">
                        <FormField left-icon="solar:lock-keyhole-bold-duotone" v-model="form.password" :label="$t('portal.customers.set-password')" type="password" :error="err('password')" :help="$t('portal.customers.password-optional-create')" />
                    </div>
                </div>
            </div>

            <div class="tw-p-4 tw-border-t tw-justify-end tw-flex tw-items-center tw-gap-2 tw-bg-gray-50">
                <a-button :disabled="loading" @click="onCancel" intent="simple" class="tw-min-w-[120px]">{{ $t('buttons.cancel') }}</a-button>
                <a-button :disabled="loading" :loading="loading" @click="onSave" class="tw-min-w-[120px]">{{ isEdit ? $t('buttons.update') : $t('buttons.create') }}</a-button>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField';
import { create, update } from '@/api/customers';
import useAlert from '@/composables/useAlert';
import { useValidation } from '@/composables/useValidation';

const props = defineProps({
    visible: { type: Boolean, default: false },
    customer: { type: Object, default: null },
});
const emit = defineEmits(['cancel', 'saved']);
const i18n = useI18n();

const loading = ref(false);
const errors = ref({});
const form = ref(defaults());
const err = useValidation(errors);
const isEdit = computed(() => !!props.customer?.id);

const paymentOptions = [
    { value: 'pending', label: i18n.t('portal.payment.pending') },
    { value: 'paid', label: i18n.t('portal.payment.paid') },
    { value: 'refunded', label: i18n.t('portal.payment.refunded') },
];

function defaults() {
    return { name: '', email: '', phone: '', country: '', note: '', payment_status: 'pending', password: '' };
}

watch(() => props.visible, (v) => {
    if (v) {
        errors.value = {};
        form.value = props.customer ? { ...defaults(), ...props.customer } : defaults();
    }
});

const onCancel = () => { if (!loading.value) emit('cancel'); };

const onSave = async () => {
    loading.value = true;
    errors.value = {};
    try {
        const payload = { ...form.value };
        const req = isEdit.value ? update(props.customer.id, payload) : create(payload);
        const { data } = await req;
        if (data.code === 'SUCCESS') {
            useAlert(i18n.t(isEdit.value ? 'messages.updated-successfully' : 'messages.created-successfully', { field: i18n.t('portal.entities.customer') }));
            emit('saved', data.customer);
        }
    } catch (e) {
        if (e.response?.status === 422) {
            for (const k in e.response.data.errors) errors.value[k] = e.response.data.errors[k][0];
        }
    } finally {
        loading.value = false;
    }
};
</script>
